import React, {useState, useEffect} from 'react';
import {AppBar, Toolbar, Typography, Menu, MenuItem, CircularProgress} from '@mui/material';
import {Box} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import jwtDecode from "jwt-decode";
import { colorGreen, colorMenuEdge, colorText, intensePaper, logoText } from "./theme.js";
import {
    getParkedDomain, getVPCService,
    isInIframe,
    isMobile,
    navigateToBaseURL,
    parseKBSubdomain, RESOURCE,
} from "./utils";

import { Logo } from "./Logo";
import {
    clientUserDataLoaded,
    goHome, isBusinessWithoutSubscription,
    isNonCloudWithoutSubscriptionOrDeveloper,
    isSharedApp,
    kbUserData
} from "../utils";
import {
    PermIdentity,
    ExitToApp,
    Wallet,
    Forum,
    PriceChangeOutlined, LightMode, DarkMode, ArrowRight, Subscriptions,
    AccountCircle
} from '@mui/icons-material';
import {useNavigate} from "react-router-dom";
import {UserAvatar} from "../User/UserAvatar";
import CloseIcon from "@mui/icons-material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import {RenderBalances} from "./Balances";
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import {NotificationsItemsList} from "./Notifications";
import {ChatsList} from "./ChatsHistory";
import {getUserNotifications, resetUnread} from "../API/APP/AppAPI";
import {getKB} from "../API/KB/KBAPI";
import {getOrgInfo} from "../API/User";
import {useTheme} from "./ThemeContext";
import {TypingLabel} from "../Components/TypingLabel";
import {isCustomChat} from "./ChatsHistoryUtils";
import {APIRequest} from "../API/APIRequest";

const KB_API_URL = getVPCService('kb') ||'https://kb.openkbs.com/';
const getDomainPublicSettings = async (domain) => {
    try {
        const response = await APIRequest('post', KB_API_URL, {
            action: 'getDomainPublicSettings',
            domain: domain
        }, { timeout: 5000 });

        return response?.data?.settings || {};
    } catch (error) {
        console.error('Error getting domain public settings:', error);
        return {};
    }
};

const Navbar = ({onLogout, chatId, setOpen, userProfile, open, balances, loadingBalances, kbSubdomain,
kbChats, setKBChats, isStreaming, navigateToChat, unreadMessages, setUserProfile, renderSettings, ChatData, setChatData,
loadMoreChats, hasMoreChats, loadingMore
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);
    const navigate = useNavigate();
    const [cacheRevision, setCacheRevision] = useState(null);
    const [picture, setPicture] = useState(null);
    const [email, setEmail] = useState(null);
    const [KBData, setKBData] = useState(null);
    const [notifAnchorEl, setNotifAnchorEl] = useState(null);
    const [phoneNotVerified, setPhoneNotVerified] = useState(false);
    const parkedDomain = getParkedDomain(window.location.hostname);
    const [companyName, setCompanyName] = useState(parkedDomain ? '' : 'OpenKBS');
    const [logoUrl, setLogoUrl] = useState(null);


    const [userNotifications, setUserNotifications] = useState(null);
    const [orgInfo, setOrgInfo] = useState(null);

    const [chatsAnchorEl, setChatsAnchorEl] = useState(null);
    const notifMenuOpen = Boolean(notifAnchorEl);
    const chatsMenuOpen = Boolean(chatsAnchorEl);

    useEffect(() => {
        getKB().then(KB => setKBData(KB))
    }, [])

    // Load organization info only if user has org
    useEffect(() => {
        const parsedSubdomain = parseKBSubdomain();
        const isKBSubdomain = !!parsedSubdomain;

        // Only fetch if user has orgName (founder) or joinedOrgId (member)
        if (!isKBSubdomain && userProfile?.email && (userProfile?.orgName || userProfile?.joinedOrgId)) {
            getOrgInfo().then(info => {
                if (info && !info.error) {
                    setOrgInfo(info);
                }
            }).catch(err => {
                console.error('Error loading org info:', err);
            });
        }
    }, [userProfile?.email, userProfile?.orgName, userProfile?.joinedOrgId])

    useEffect(() => {
        // Load company name from public settings
        const parsedSubdomain = parseKBSubdomain();
        const isKBSubdomain =  !!parsedSubdomain;

        if (parkedDomain && !isKBSubdomain) {
            getDomainPublicSettings(parkedDomain).then(settings => {
                const name = settings?.COMPANY_NAME || settings?.companyName || parkedDomain;
                setCompanyName(name);
            }).catch(err => {
                console.error('Error loading public settings:', err);
                // Use domain as fallback for parked domains
                setCompanyName(parkedDomain);
            });

            // Check if custom logo exists
            const logoPath = `https://${parkedDomain}/logo.png`;
            const img = new Image();
            img.onload = () => {
                setLogoUrl(logoPath);
            };
            img.onerror = () => {
                setLogoUrl(null);
            };
            img.src = logoPath;

            // Load custom favicon
            const faviconPath = `https://${parkedDomain}/favicon.ico`;
            const faviconImg = new Image();
            faviconImg.onload = () => {
                // Set favicon dynamically
                let link = document.querySelector("link[rel~='icon']");
                if (!link) {
                    link = document.createElement('link');
                    link.rel = 'icon';
                    document.head.appendChild(link);
                }
                link.href = faviconPath;
            };
            faviconImg.src = faviconPath;
        }
    }, [])

    useEffect(() => {
        // This empty useEffect will cause a re-render when unreadMessages changes
    }, [unreadMessages]);

    const handleChatsMenuOpen = (event) => {
        setChatsAnchorEl(event.currentTarget);
    };

    const handleChatsMenuClose = () => {
        setChatsAnchorEl(null);
    };

    const handleNotifMenuOpen = (event) => {
        setNotifAnchorEl(event.currentTarget);
        if (userProfile?.unread > 0) {
            setUserProfile(prev => {
                return {
                    ...prev,
                    unread: 0
                }
            })
        }
        resetUnread();
        getUserNotifications().then(data => {
            setUserNotifications(data)

        })

    };

    const handleNotifMenuClose = () => {
        setNotifAnchorEl(null);
    };

    const handleDrawerOpenClose = () => {
        if (!isMobile) {
            setOpen(!open);
        }
    };

    useEffect(() => {
        const handleCacheInvalidation = (event) => {
            setCacheRevision(event.detail.cacheRevision);
        };
        window.addEventListener('userImageCacheInvalidated', handleCacheInvalidation);
        return () => {
            window.removeEventListener('userImageCacheInvalidated', handleCacheInvalidation);
        };
    }, []);

    const menuItemStyle = { padding: '10px 30px' };
    const parsedSubdomain = parseKBSubdomain();
    const isKBSubdomain =  !!parsedSubdomain;

    // set early picture from JWT
    useEffect(() => {
        // deprecated
        // if (getQueryParamValue('pnv')) {
        //     navigate('/')
        //     setPhoneNotVerified(true)
        // }

        if (clientUserDataLoaded?.picture && clientUserDataLoaded?.email) {
            setPicture(clientUserDataLoaded?.picture)
            setEmail(clientUserDataLoaded?.email)
        }
    }, []);

    useEffect(() => {
        if (isKBSubdomain) {
            setPicture(kbUserData()?.userPicture || '')
            setEmail(kbUserData()?.email)
        } else {
            try {
                const token = localStorage.getItem("clientJWT");
                setPicture((userProfile?.picture || (userProfile?.picture === '' && jwtDecode(token)?.picture)))
                setEmail(userProfile?.email)
            } catch(e) {
                window.dispatchEvent(new CustomEvent('logout'));
            }
        }
    }, [userProfile]);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const runningOutOfTokens = balances?.find(o => o.resourceId === RESOURCE.tokens)?.balance < 30000;

    const { toggleTheme, theme } = useTheme();
    const totalUnread = Object.values(unreadMessages)
        .reduce((accumulator, currentValue) => (accumulator + currentValue),0)

    const showSubscribeColored = runningOutOfTokens // && isNonCloudWithoutSubscriptionOrDeveloper(userProfile);

    return (
        <AppBar elevation={1} position="fixed" sx={{zIndex: (theme) => theme.zIndex.drawer + 1}}>
            <Box
                sx={{
                    backgroundColor: renderSettings?.navbarBackgroundColor || (theme => theme.palette.background.paper),
                    // minHeight: '100vh',
                    borderBottom: orgInfo?.orgColor ? `3px solid ${orgInfo.orgColor}` : 'none',
                }}
            >
                <Toolbar>

                    {((isMobile && isKBSubdomain && !renderSettings?.disableMobileChatHistory)) && <IconButton
                        style={{marginLeft: '-12px'}}
                        size="large"
                        aria-label="show chat messages"
                        color="inherit"
                        onClick={handleChatsMenuOpen}
                    >
                        <Badge
                            max={999}
                            key={`totalUnread-${totalUnread}`}
                            sx={{
                                '& .MuiBadge-badge': {
                                    animation: totalUnread ? 'bubble 1s ease-in-out' : 'none',
                                },
                            }}
                            badgeContent={totalUnread}
                            color="error">
                            <Forum />
                            {/*{ChatData?.chatIcon ? <span style={{fontSize: 22}}>{ChatData.chatIcon}</span> : <Forum /> }*/}
                        </Badge>
                    </IconButton>}

                    {!isMobile && !renderSettings?.disableLeftMenu && <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpenClose}
                        edge="start"
                        sx={{mr: 2}}
                    >
                        {open ? <CloseIcon /> : <MenuIcon />}
                    </IconButton>}

                    <div style={{width: (isMobile || renderSettings?.disableLeftMenu) ? '8px' : (!open ? '0px' : '260px'), transition: 'width 0.130s linear'}} ></div>

                    {KBData?.kbTitle
                        ? (
                            <>
                                <div style={{ marginLeft: 50, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', position: isMobile ? 'absolute' : undefined }}>
                                    <Typography variant="h5" sx={{
                                        fontSize: 18,
                                        fontFamily: "'Roboto', sans-serif",
                                        fontWeight: 600,
                                        color: colorText,
                                        cursor: 'pointer',
                                        maxWidth: isMobile ? 180 : 'none',
                                        overflow: isMobile ? 'hidden' : 'visible',
                                        textOverflow: isMobile ? 'ellipsis' : 'clip',
                                        whiteSpace: isMobile ? 'nowrap' : 'normal'
                                    }}>
                                        {!isMobile && ChatData?.chatIcon} { isCustomChat(ChatData) ? ChatData?.title : KBData?.kbTitle}
                                    </Typography>
                                </div>
                            </>
                        )
                        : !isKBSubdomain && (<>
                            {!parkedDomain && (
                                <>
                                    <IconButton
                                        style={{marginLeft: '-18px'}}
                                        color="inherit"
                                        aria-label="logo"
                                        onClick={() => goHome(navigate)}
                                        edge="start"
                                        sx={{mr: 2}}
                                    >
                                        <Logo width="24" height="24" />
                                    </IconButton>
                                    <Typography variant="h5" sx={{
                                        fontSize: isMobile ? 18 : 20,
                                        marginLeft: '-12px',
                                        marginTop: '2px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        color: logoText
                                    }}
                                                onClick={() => goHome(navigate)}
                                    >
                                        {companyName}
                                    </Typography>
                                </>
                            )}

                            {parkedDomain && logoUrl && (
                                <img
                                    src={logoUrl}
                                    alt="Logo"
                                    height="32"
                                    style={{cursor: 'pointer', marginLeft: '-10px'}}
                                    onClick={() => goHome(navigate)}
                                />
                            )}

                            {parkedDomain && !logoUrl && (
                                <Typography variant="h5" sx={{
                                    fontSize: isMobile ? 18 : 20,
                                    marginLeft: '-12px',
                                    marginTop: '2px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    color: logoText
                                }}
                                            onClick={() => goHome(navigate)}
                                >
                                    {companyName}
                                </Typography>
                            )}

                            {/* Organization name display */}
                            {orgInfo?.orgName && (
                                <Typography variant="body2" sx={{
                                    fontSize: 12,
                                    marginLeft: '12px',
                                    marginTop: '2px',
                                    color: colorText,
                                    opacity: 0.7,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {orgInfo.orgName}
                                </Typography>
                            )}
                        </>)}

                    <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                        <div style={{visibility: (renderSettings?.disableBalanceView || userProfile?.subscriptionPlan) ? 'hidden' : undefined}}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginRight: '16px' }}>
                                {/* Conditional rendering based on your existing logic */}
                                {(!kbSubdomain || !isSharedApp) && <span style={{ marginRight: isMobile ? 10 : 0 }}>{RenderBalances({ balances, loadingBalances, kbSubdomain })}</span>}
                            </Box>
                        </div>

                        {((isMobile && kbSubdomain) || (!isMobile && kbSubdomain && renderSettings?.disableLeftMenu)) && <>
                        <Menu
                            anchorEl={chatsAnchorEl}
                            open={chatsMenuOpen}
                            onClose={handleChatsMenuClose}
                            slotProps={{
                                paper: {
                                    sx: {
                                        backgroundColor: intensePaper
                                    }
                                }
                            }}
                        >
                            <ChatsList {...{kbChats, setKBChats, isStreaming, navigateToChat, unreadMessages, ChatData, setChatData, renderSettings, loadMoreChats, hasMoreChats, loadingMore}}
                                       onClickChat={handleChatsMenuClose}
                            />
                        </Menu></>}

                        {!isKBSubdomain && userProfile?.email && !parkedDomain && <>
                            <IconButton
                                style={{ marginRight: '8px' }}
                                size="large"
                                aria-label="show new notifications"
                                color="inherit"
                                onClick={handleNotifMenuOpen}
                            >
                                <Badge badgeContent={userProfile?.unread} color="error">
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                            <Menu
                                anchorEl={notifAnchorEl}
                                open={notifMenuOpen}
                                onClose={handleNotifMenuClose}
                                PaperProps={{
                                    sx: {
                                        backgroundColor: colorMenuEdge,
                                    }
                                }}
                            >
                                {/* Check if userNotifications is null (loading state) */}
                                {userNotifications === null ? (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        margin: 30,
                                        minWidth: 300,
                                        minHeight: 100
                                    }}>
                                        <CircularProgress />
                                    </div>
                                ) : userNotifications.length === 0 ? (
                                    // Check if userNotifications is an empty array (no notifications)
                                    <div style={{ margin: 30, minWidth: 300, minHeight: 100 }}>There are no notifications yet.</div>
                                ) : (
                                    // Render notifications if there are any
                                    <NotificationsItemsList notifications={userNotifications} unread={userProfile?.unread} />
                                )}
                            </Menu>
                        </>}

                        {(!(renderSettings?.disableMobileTopRightMenu && isMobile)) &&
                            <div style={{ position: 'relative' }}>
                                {picture ? (
                                    <UserAvatar picture={picture} onClick={handleMenu} style={{ cursor: 'pointer' }} />
                                ) : (
                                    <IconButton
                                        onClick={handleMenu}
                                        style={{ padding: 4 }}
                                    >
                                        <AccountCircle style={{ width: 40, height: 40, color: colorText }} />
                                    </IconButton>
                                )}
                                {(runningOutOfTokens || phoneNotVerified) && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: '-10px',
                                            top: '-10px',
                                            backgroundColor: '#f0ad4e', // warning color
                                            color: 'black',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            whiteSpace: 'nowrap',
                                            zIndex: 10,
                                        }}
                                        onClick={handleMenu}
                                    >
                                        {runningOutOfTokens ? "Buy Credits" : "Buy Credits"}
                                    </div>
                                )}
                            </div>
                        }
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleClose}
                        onClick={handleClose}
                    >
                        <MenuItem disabled={true} selected={false} sx={menuItemStyle}>
                            {email}
                        </MenuItem>

                        <MenuItem onClick={toggleTheme} sx={menuItemStyle}>
                            {theme.palette.mode  === 'dark' ? <LightMode sx={{ mr: 1 }} /> : <DarkMode sx={{ mr: 1 }} /> }
                            {theme.palette.mode  === 'dark' ? 'Light' : 'Dark' } Theme
                        </MenuItem>

                        <MenuItem onClick={() => !isKBSubdomain ? navigate('/profile') : navigateToBaseURL('/profile', isInIframe())}
                                  sx={menuItemStyle}>
                            <PermIdentity sx={{ mr: 1 }} />
                            Profile
                        </MenuItem>
                        <MenuItem
                            onClick={() => !isKBSubdomain
                                ? navigate('/billing')
                                : navigateToBaseURL('/billing' + (phoneNotVerified ? '?showPhoneVerification=1' : ''),
                                    (isInIframe() || phoneNotVerified || runningOutOfTokens))
                            }
                            sx={{
                                ...menuItemStyle,
                                backgroundColor: (phoneNotVerified) ? '#fff3e0' : 'inherit',
                                color: (phoneNotVerified) ? colorGreen : 'inherit',
                                fontWeight: (phoneNotVerified) ? 'bold' : 'normal'
                            }}
                        >
                            <Wallet sx={{ mr: 1, color: (phoneNotVerified) ? colorGreen : undefined }} />
                            Billing

                            {phoneNotVerified && <span style={{ marginLeft: '8px', color: colorGreen }}>
                             <ArrowRight style={{marginBottom: -7, color: colorGreen}}/>Activate Bonus</span>}

                            {/*{runningOutOfTokens && !phoneNotVerified && <span style={{ marginLeft: '8px' }}>*/}
                            {/*    {!isBusinessWithoutSubscription(userProfile) && <><ArrowRight style={{marginBottom: -7}}/> Buy Tokens</>}</span>}*/}
                        </MenuItem>
                        <MenuItem
                            onClick={() => !isKBSubdomain ? navigate('/subscriptions') : navigateToBaseURL('/subscriptions', isInIframe())}
                            sx={{
                                ...menuItemStyle,
                                backgroundColor: (showSubscribeColored) ? '#fff3e0' : 'inherit',
                                color: (showSubscribeColored) ? '#f57c00' : 'inherit',
                                fontWeight: (showSubscribeColored) ? 'bold' : 'normal'
                            }}
                        >
                            <Subscriptions sx={{ mr: 1, color: (showSubscribeColored) ? '#f57c00' : undefined }} />
                            Subscriptions

                            {showSubscribeColored && <span style={{ marginLeft: '8px', color: '#f57c00' }}>
                            <ArrowRight style={{marginBottom: -7, color: '#f57c00'}}/> Pay Now</span>}
                        </MenuItem>
                        <MenuItem onClick={() => onLogout({fromLogoutButton: true})} sx={menuItemStyle}>
                            <ExitToApp sx={{ mr: 1 }} />
                            {!isKBSubdomain ? `Logout` : `Logout from ${document.title}`}
                        </MenuItem>

                    </Menu>
                </Toolbar>
            </Box>
        </AppBar>
    );
};

export default Navbar;